import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Customer } from '@usf/customer-types';
import {
  MyOrdersStoreService,
  OrderState,
  OrderStatus,
  OrderReviewStoreService,
} from '@usf/ngrx-order';
import { Observable, Subscription } from 'rxjs';
import { filter, take, tap, map } from 'rxjs/operators';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import {
  CustomerOrdersViewModel,
  PastOrdersRow,
} from 'src/app/shared/models/my-orders-view.model';
import { OrderService } from '../../../order/services/order.service';
import { ModalWithNavComponent } from '../../../shared/components/modal-with-nav/modal-with-nav.component';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { PATHS } from '../../../shared/constants/paths';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { HelpModalDesktopComponent } from '../../../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../../../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { NotificationsService } from '@notifications/services/notifications.service';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { CopyOrderModalDesktopComponent } from '@app/my-orders/copy-order-modal/copy-order-modal-desktop/copy-order-modal-desktop.component';
import { CopyOrderModalTouchComponent } from '@app/my-orders/copy-order-modal/copy-order-modal-touch/copy-order-modal-touch.component';
import { OrderTracingService } from '../../../order/tracking/tracing/order-tracing.service';
import { MyOrdersAnalyticsService } from '@app/my-orders/tracking/analytics/my-orders-analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@app/user/services';

@Component({
  selector: 'app-my-orders-view',
  templateUrl: './my-orders-view.page.html',
  styleUrls: ['./my-orders-view.page.scss'],
})
export class MyOrdersViewPage implements OnInit {
  static MAX_DATA_REFRESH_CALLS = 3;
  static DATA_REFRESH_INTERVAL = 5 * 60 * 1000;

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  breadcrumbs: Crumb[];
  customers: Customer[] = [];
  ordersListSubscription$: Subscription;
  ordersLoading$: Subscription;
  reviewedOrdersLoadingSubscription$: Subscription;
  myOrders: CustomerOrdersViewModel;
  modalOpen = false;
  numberOfSelectedFilters = 0;
  activeTab: string;
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  viewPort: CdkVirtualScrollViewport;
  orderStatusEnum = OrderStatus;
  toggleValue: string;
  refreshDataIntervalRef;
  refreshDataCount = 0;
  isSuperUser$: Observable<boolean>;
  isNotSuperUser$: Observable<boolean>;
  isTouchExperience$: Observable<boolean>;
  isNotTouchExperience$: Observable<boolean>;
  isTablet$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  orderReviewLoading = false;
  loading: boolean;
  myOrdersRefresherFlag = FEATURES.split_global_my_orders_refresher.name;
  pastOrders: boolean;
  hideRefresher = false;
  tabAnimate: boolean;

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    readonly userService: UserService,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderService: OrderService,
    private router: Router,
    private modalController: ModalController,
    private cdRef: ChangeDetectorRef,
    private modalService: ModalService,
    private notificationsService: NotificationsService,
    private orderTracingService: OrderTracingService,
    private myOrdersAnalyticsService: MyOrdersAnalyticsService,
    private translateService: TranslateService,
    private orderReviewStoreService: OrderReviewStoreService,
  ) {}

  ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '',
        active: true,
      },
    ];

    // Create derived observables for platform and user state
    this.isTouchExperience$ = this.platformService.isTouchExperience$.pipe(
      filter(value => value !== null),
    );
    this.isNotTouchExperience$ = this.isTouchExperience$.pipe(
      map(isTouch => !isTouch),
    );

    // Create observables for tablet and mobile detection
    this.isTablet$ = this.isTouchExperience$.pipe(
      filter(() => this.platformService.platformEnum?.tablet !== null),
      map(
        () =>
          this.platformService.platformType ===
          this.platformService.platformEnum?.tablet,
      ),
    );

    this.isMobile$ = this.isTouchExperience$.pipe(
      filter(() => this.platformService.platformEnum?.mobile !== null),
      map(
        () =>
          this.platformService.platformType ===
          this.platformService.platformEnum?.mobile,
      ),
    );
  }

  reloadList() {
    if (this.viewPort) {
      this.viewPort.checkViewportSize();
      this.cdRef.detectChanges();
      setTimeout(() => {
        this.enableRefresher();
      });
    }
  }

  ionViewWillEnter() {
    this.pastOrders = false;
    this.activeTab = 'current';
    this.tabAnimate = false;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.myOrders),
    );
    this.isSuperUser$ = this.userService.isSuperUser$();
    this.isNotSuperUser$ = this.isSuperUser$.pipe(
      filter(value => value !== null),
      map(isSuperUser => !isSuperUser),
    );
    this.myOrdersStoreService.retrieveCustomerOrders();
    this.ordersListSubscription$ = this.orderService
      .loadCustomerOrdersViewModel()
      .subscribe(customerOrdersVm => {
        this.customers = customerOrdersVm.customers;
        this.myOrders = customerOrdersVm;
        this.reloadList();
      });
    this.notificationsService.checkMissedNotification();

    this.reviewedOrdersLoadingSubscription$ =
      this.orderReviewStoreService.loading$.pipe().subscribe(loading => {
        this.orderReviewLoading = loading;
      });

    if (this.userService.isInternalUser && !this.orderReviewLoading) {
      this.orderReviewStoreService.getOrderReviewPreferences();
    }

    this.refreshDataCount = 0;
    this.refreshDataIntervalRef = setInterval(() => {
      this.refreshDataCount++;
      if (this.refreshDataCount > MyOrdersViewPage.MAX_DATA_REFRESH_CALLS) {
        clearInterval(this.refreshDataIntervalRef);
      } else {
        this.myOrdersStoreService.retrieveCustomerOrders();
      }
    }, MyOrdersViewPage.DATA_REFRESH_INTERVAL);
  }

  ionViewDidEnter() {
    window.dispatchEvent(new Event('resize'));
    this.toggleValue = 'myOrders';
    this.enableRefresher();
  }

  ionViewWillLeave() {
    if (this.ordersListSubscription$) {
      this.ordersListSubscription$.unsubscribe();
    }
    if (this.reviewedOrdersLoadingSubscription$) {
      this.reviewedOrdersLoadingSubscription$.unsubscribe();
    }
    if (this.ordersLoading$) {
      this.ordersLoading$.unsubscribe();
    }

    if (this.refreshDataIntervalRef) {
      clearInterval(this.refreshDataIntervalRef);
    }
  }

  createNewOrder(): void {
    this.orderService
      .shouldShowCartSyncingPopover$()
      .pipe(take(1))
      .subscribe(async showPopover => {
        if (showPopover) {
          await this.presentSyncingModal();
        } else {
          this.orderService.createNewOrder();
          this.routeTo(PATHS.LISTS);
        }
      });
  }

  routeTo(path: string) {
    this.router.navigate([path]);
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  goToTop() {
    this.platformService?.platformType ===
    this.platformService.platformEnum.desktop
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  async presentSyncingModal() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      { customerSwitch: false, orderSwitch: true },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.continueClick) {
        this.orderService.createNewOrder();
        this.routeTo(PATHS.LISTS);
      }
    });
    return await modal.present();
  }

  switchTab(event: any) {
    if (event === 'current') {
      if (this.activeTab !== 'past') {
        this.myOrdersAnalyticsService.trackPastOrdersTabClick();
      }
      this.activeTab = 'past';
    } else {
      this.activeTab = 'current';
    }
  }

  async openCopyOrderModal(pastOrderRow: PastOrdersRow) {
    this.ordersLoading$ = this.myOrdersStoreService
      .state$()
      .subscribe(state => {
        this.loading =
          state?.customerOrdersLoading || state?.recentOrdersLoading;
      });
    this.orderTracingService.traceOrderCardClickedOnPastOrders(
      pastOrderRow?.orderHeader.orderId,
      pastOrderRow?.orderHeader.origOrderId,
      pastOrderRow?.orderHeader.origSplitOrderId,
      pastOrderRow?.orderHeader.totalPrice,
      pastOrderRow?.orderHeader.tandemOrderNumber,
      pastOrderRow?.orderHeader.customerNumber,
      pastOrderRow?.orderHeader.departmentNumber,
      pastOrderRow?.orderHeader.divisionNumber,
      pastOrderRow?.orderHeader.requestedDeliveryDate,
      pastOrderRow?.orderHeader.confirmedDeliveryDate,
    );
    this.myOrdersAnalyticsService.trackCopyOrderModalOpen(
      pastOrderRow?.orderHeader,
    );
    const productMap$ = this.orderService.getProductMapWithOrderItems$(
      pastOrderRow.orderItems,
    );

    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: CopyOrderModalTouchComponent,
        rootPageParams: {
          pastOrderRow,
          productMap$,
          ordersLoading: this.loading,
        },
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: CopyOrderModalDesktopComponent,
          rootPageParams: {
            pastOrderRow,
            productMap$,
            ordersLoading: this.loading,
          },
        },
        'modal-desktop-size-lg',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  enableRefresher() {
    if (!this.viewPort) return;

    this.viewPort.elementScrolled().subscribe(scrollEvent => {
      const scrollY = this.viewPort.measureScrollOffset('top');
      const newValue = scrollY !== 0;

      if (this.hideRefresher !== newValue) {
        setTimeout(() => {
          this.hideRefresher = newValue;
          this.cdRef.markForCheck();
        });
      }
    });
  }

  handleRefresh(event: Event) {
    if (!this.orderService.networkStatus) {
      (event?.target as any)?.complete();
    } else {
      if (this.userService.isInternalUser && !this.orderReviewLoading) {
        this.orderReviewStoreService.getOrderReviewPreferences();
      }
      this.myOrdersStoreService.retrieveCustomerOrders();
      this.myOrdersStoreService
        .state$()
        .pipe(
          filter((state: OrderState) => !state?.customerOrdersLoading),
          take(1),
        )
        .subscribe(() => {
          (event?.target as any)?.complete();
          this.reloadList();
        });
    }
  }
  switchTabTouchView() {
    this.pastOrders = !this.pastOrders;
    this.tabAnimate = true;
    this.reloadList();
  }
}
